import {
  Logger,
  LoggerContext,
  TimerContext,
  useAppComponents,
  useEnvironment,
  useLogger,
  useTimer,
} from "@amzn/xb-hooks";
import React, { useContext } from "react";
import { useExperienceProviders } from "../experience-providers";
import { createConsoleLogger, createRumLogger } from "../loggerFactories";
import { useTimerHookProvider } from "../timing";
import { ContainerLoadingMetrics, findXpsComponentEndpoint } from "./federation";
import { LazyRemoteComponentStore } from "./LazyRemoteComponentStore";
import { LiveComponentSourceProps, UpperBound } from "./LiveComponentSource";
import { LiveContext } from "./LiveContext";

export interface RemoteComponentInfo {
  endpoint: string;
  experienceProviderName: string;
  componentName: string;
  /**
   * This fallback is for while the XPS code is being pulled down. Fallbacks for internal state
   * changes (such as  for while an api call resolves) remain the responsibility of the component.
   * We now want people to use the () => ReactNode, though we will allow just a ReactNode for backward compat.
   */
  fallback?: React.ReactNode | (() => React.ReactNode);
}

export interface RemoteComponentProps<T extends UpperBound = UpperBound> extends Omit<RemoteComponentInfo, "endpoint"> {
  /**
   * A list of fields that do not error when overridden inside of Remote components.
   */
  fieldOverrideAllowList?: string[];

  /**
   * @deprecated use the {@link LiveComponentSourceProps.flavor} instead
   */
  flavor?: string;
  /**
   * @deprecated use the {@link LiveComponentSourceProps.payload} instead.
   */
  payload?: string;
  /**
   * All the props that will be sent to LiveComponentSource.
   */
  props?: LiveComponentSourceProps<T>;
}

const RemoteComponentComponentInternal = ({
  experienceProviderName,
  componentName,
  fallback,
  props, // we spread "props" into the top level for the live component
  ...rest
}: RemoteComponentProps) => {
  const experienceProviders = useExperienceProviders();
  const { Tier2LiveComponentErrorFallback } = useAppComponents();
  const { log, count } = useLogger();

  /*
   * Creating an instance for logging various microfrontend loading metrics, so we can
   * pass the reference to the various logging hooks.
   * The proper way to do this would probably be to refactor the lazy
   * component loading logic into a custom hook so we don't have to prop drill.
   * But going to leave that to future work for now.
   */
  const initRemoteComponentTimer = useTimer(`RemoteComponent-Init-Time`);
  const loadRemoteComponentTimer = useTimer(`RemoteComponent-Load-Time`);
  const metricsInstance: ContainerLoadingMetrics = {
    initRemoteComponentTimer,
    loadRemoteComponentTimer,
    recordInitSuccess: () => count(`RemoteComponent-Init-Success`),
    recordInitFailure: () => count(`RemoteComponent-Init-Failure`),
    recordLoadSuccess: () => count(`RemoteComponent-Load-Success`),
    recordLoadFailure: () => count(`RemoteComponent-Load-Failure`),
  };

  log.debug(`Looking up the endpoint for ${experienceProviderName}(XPS).`);
  const endpoint = findXpsComponentEndpoint(experienceProviderName, experienceProviders);
  log.info(`Found ${endpoint}(endpoint) from ${experienceProviders}.`);

  if (!endpoint) {
    log.error(`No endpoint found for ${experienceProviderName} from ${experienceProviders}.`);
    return <Tier2LiveComponentErrorFallback />;
  }

  const LazyComponent = LazyRemoteComponentStore.populateAndGet(
    {
      endpoint,
      experienceProviderName,
      componentName,
      fallback,
    },
    metricsInstance
  );

  return <LazyComponent {...rest} {...props} />;
};

export function RemoteComponent(props: RemoteComponentProps) {
  const { xbAppName } = useContext(LiveContext);
  const { isDev } = useEnvironment();
  const { experienceProviderName, componentName } = props;
  const logger: Logger = isDev()
    ? createConsoleLogger({ xpsName: experienceProviderName, componentName })
    : createRumLogger({ xbAppName, xpsName: experienceProviderName, componentName });

  return (
    <LoggerContext.Provider value={logger}>
      <TimerContext.Provider value={useTimerHookProvider}>
        <RemoteComponentComponentInternal {...props}></RemoteComponentComponentInternal>
      </TimerContext.Provider>
    </LoggerContext.Provider>
  );
}
